/**
 * Portfolio Dashboard Presenter
 * Pure presentational component using refactored architecture
 * Maintains exact same interface as original PortfolioDashboard
 * TASK-207: Added PortfolioFilters integration
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Layout components (reuse existing)
import SecondaryNav from '../../../components/layout/SecondaryNav';
import { portfolioApiService } from '../../../services/api/portfolioApiService';

// TASK-207: Portfolio filters
import PortfolioFilters, { ClientOption, PortfolioOption } from '../../../components/portfolio/filters/PortfolioFilters';
import {
  usePortfolioFilters,
  useFilterActions,
  useViewContext,
} from '../store/portfolioStore';

// Portfolio components (reuse existing)
import PortfolioInsightsTab from '../../../components/portfolio/InsightsTab';
import CompositionTab from '../../../components/portfolio/CompositionTab';
import PerformanceTab from '../../../components/portfolio/PerformanceTab';
import RiskTab from '../../../components/portfolio/RiskTab';
import ClientsTab from '../../../components/portfolio/ClientsTab';
import HistoryTab from '../../../components/portfolio/HistoryTab';
import PortfolioAIChat from '../../../components/portfolio/PortfolioAIChat';
import MyBookView from '../../../components/portfolio/MyBookView';
import PersonalizationTab from '../../../components/portfolio/PersonalizationTab';

// TASK-277: Removed mock data imports - using real API data only

// Use refactored services
import { ClientDashboardData, GlobalDashboardData } from '../aggregation/PortfolioAggregator';

interface PortfolioDashboardPresenterProps {
  selectedPortfolio: string;
  timeRange: string;
  activeTab: string;
  loading: boolean;
  clientData: ClientDashboardData | null;
  globalData: GlobalDashboardData | null;
  clients: Array<{ id: string; name: string }>;
  onPortfolioChange: (portfolio: string) => void;
  onTimeRangeChange: (range: string) => void;
  onTabChange: (tab: string) => void;
  onClientSelect: (clientId: string) => void;
  onRefresh: () => void;
  benchmark?: string;
  onBenchmarkChange?: (benchmark: string) => void;
}

const PortfolioDashboardPresenter: React.FC<PortfolioDashboardPresenterProps> = ({
  selectedPortfolio,
  timeRange: _timeRange,
  activeTab,
  loading,
  clientData,
  globalData,
  clients,
  onPortfolioChange: _onPortfolioChange,
  onTimeRangeChange: _onTimeRangeChange,
  onTabChange,
  onClientSelect,
  onRefresh: _onRefresh,
  benchmark,
  onBenchmarkChange,
}) => {

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // TASK-207: Get portfolio filters and view context from store
  const portfolioFilters = usePortfolioFilters();
  const { setPortfolioFilters } = useFilterActions();
  const viewContext = useViewContext();

  // AI Chat: highlight & scroll state
  const [highlightedSection, setHighlightedSection] = useState<string>('');
  const [scrollTarget, setScrollTarget] = useState<string>('');

  // Refs for scroll-to-section
  const insightsRef = useRef<HTMLDivElement>(null);
  const compositionRef = useRef<HTMLDivElement>(null);
  const performanceRef = useRef<HTMLDivElement>(null);
  const riskRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);
  const personalizationRef = useRef<HTMLDivElement>(null);

  // TASK-334: Determine mode based on viewContext, not just selectedPortfolio
  // This ensures navigation via ViewLevelSelector properly updates the view
  const isGlobalMode = viewContext.level === 'my-book' || (selectedPortfolio === 'all' && !viewContext.portfolioId);
  const isPortfolioLevel = viewContext.level === 'portfolio' && !!viewContext.portfolioId;

  // TASK-277: Fetch ALL clients for ViewLevelSelector navigation
  const { data: allClientsData } = useQuery({
    queryKey: ['all-clients-for-nav'],
    queryFn: () => portfolioApiService.getMyBookSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // TASK-277: Fetch client portfolios for ViewLevelSelector when on portfolio level
  // This ensures portfolio dropdown remains populated after navigating to portfolio level
  const { data: clientPortfoliosData } = useQuery({
    queryKey: ['client-portfolios-for-nav', viewContext.clientId],
    queryFn: () => viewContext.clientId
      ? portfolioApiService.getClientPortfolios(viewContext.clientId)
      : Promise.resolve({ portfolios: [], count: 0 }),
    staleTime: 5 * 60 * 1000,
    enabled: !!viewContext.clientId, // Only fetch when we have a client context
  });

  // TODO: Fetch from API when available (GET /api/portfolio/:id/accounts)
  const availableAccounts = React.useMemo(() => {
    if (!selectedPortfolio || selectedPortfolio === 'all') return [];
    return [] as Array<{ id: string; name: string; type: 'investment' | 'cash'; currency: string; enabled: boolean }>;
  }, [selectedPortfolio]);

  // TASK-207: Update filters.accounts when available accounts change
  React.useEffect(() => {
    if (availableAccounts.length > 0 && portfolioFilters.accounts.length === 0) {
      setPortfolioFilters({ accounts: availableAccounts });
    }
  }, [availableAccounts, portfolioFilters.accounts.length, setPortfolioFilters]);

  // Extract data from clientData or use defaults
  const overview = clientData?.overview || null;
  const positions = clientData?.positions || [];
  const allocation = clientData?.allocation || null;
  const cashPositions = clientData?.cash || [];
  const performance = clientData?.performance || null;
  const performanceHistory = clientData?.performanceHistory || [];
  const performanceAttribution = clientData?.performanceAttribution || null;
  const rollingReturns = clientData?.rollingReturns || [];
  const rollingReturns12m = clientData?.rollingReturns12m || [];
  // Recalculate contributors from positions using contribution to portfolio return (P&L / totalAuM)
  // Backend returns individual security returns (P&L / book_value) which can be misleading (e.g. 7040% for small positions)
  const { topContributors, bottomContributors } = React.useMemo(() => {
    const totalAuM = overview?.total_value || positions.reduce((sum, p) => sum + (p.market_value || 0), 0);
    const sorted = [...positions].sort((a, b) => (b.unrealized_pl || 0) - (a.unrealized_pl || 0));
    const top = sorted
      .filter((p) => (p.unrealized_pl || 0) > 0)
      .slice(0, 5)
      .map((p) => ({
        name: p.security_name,
        contribution: totalAuM > 0 ? ((p.unrealized_pl || 0) / totalAuM) * 100 : 0,
      }));
    const bottom = sorted
      .filter((p) => (p.unrealized_pl || 0) < 0)
      .slice(-5)
      .reverse()
      .map((p) => ({
        name: p.security_name,
        contribution: totalAuM > 0 ? ((p.unrealized_pl || 0) / totalAuM) * 100 : 0,
      }));
    return { topContributors: top, bottomContributors: bottom };
  }, [positions, overview]);
  const riskMetrics = clientData?.risk || null;
  const stressTests = clientData?.stressTests || null;
  const correlationMatrix = clientData?.correlationMatrix || null;
  const varDistribution = clientData?.varDistribution || [];
  const transactions = clientData?.transactions || [];

  // Extract global data
  const clientPortfolios = globalData?.portfolios || [];

  // TASK-277: Use ALL clients for ViewLevelSelector navigation (not just current)
  const clientOptions: ClientOption[] = React.useMemo(() => {
    // Primary: Use all clients from my-book summary for navigation
    if (allClientsData?.clients) {
      return allClientsData.clients.map((client: { client_id: string; client_name: string; total_value: number }) => ({
        id: client.client_id,
        name: client.client_name || client.client_id,
        totalAuM: client.total_value || 0
      }));
    }

    // Fallback: Build from global data if my-book not loaded yet
    const clientsMap = new Map<string, ClientOption>();

    if (globalData?.portfolios) {
      globalData.portfolios.forEach(portfolio => {
        const clientId = portfolio.client_id;
        const clientName = portfolio.client_name;

        if (clientId && clientName) {
          const existing = clientsMap.get(clientId);
          if (existing) {
            const portfolioValue = portfolio.total_value || 0;
            existing.totalAuM = (existing.totalAuM || 0) + portfolioValue;
          } else {
            const portfolioValue = portfolio.total_value || 0;
            clientsMap.set(clientId, {
              id: clientId,
              name: clientName,
              totalAuM: portfolioValue
            });
          }
        }
      });
    }

    // Also add clients from the clients prop
    clients.forEach(client => {
      if (!clientsMap.has(client.id)) {
        clientsMap.set(client.id, {
          id: client.id,
          name: client.name,
          totalAuM: undefined
        });
      }
    });

    return Array.from(clientsMap.values());
  }, [allClientsData, globalData, clients]);

  const portfolioOptions: PortfolioOption[] = React.useMemo(() => {
    // TASK-277: First try to get portfolios from dedicated client portfolios query
    // This ensures dropdown works even when on portfolio level (where globalData is null)
    if (clientPortfoliosData?.portfolios && clientPortfoliosData.portfolios.length > 0) {
      return clientPortfoliosData.portfolios.map((portfolio: any) => ({
        id: portfolio.id || portfolio.portfolio_id,
        name: portfolio.name || portfolio.portfolio_name,
        clientId: viewContext.clientId || portfolio.client_id,
        value: portfolio.total_value || portfolio.value || undefined
      })).filter((p: PortfolioOption) => p.id && p.name);
    }

    // Fallback to globalData if available
    if (!globalData?.portfolios) return [];

    const portfolios = globalData.portfolios.map(portfolio => {
      const portfolioId = portfolio.id || portfolio.portfolio_id;
      const portfolioName = portfolio.name || portfolio.portfolio_name;
      const clientId = portfolio.client_id;
      const portfolioValue = portfolio.total_value;

      return {
        id: portfolioId,
        name: portfolioName,
        clientId: clientId,
        value: portfolioValue || undefined
      };
    }).filter(p => p.id && p.name && p.clientId);

    const allPortfolios = portfolios;

    // If we have a client context, filter portfolios to only that client
    if (viewContext.clientId) {
      return allPortfolios.filter(p => p.clientId === viewContext.clientId);
    }

    // Otherwise show all portfolios
    return allPortfolios;
  }, [viewContext.clientId, globalData, clientPortfoliosData]);

  // AI Chat handlers
  const handleAITabChange = (tab: string) => {
    onTabChange(tab);
  };

  const handleAIFilterChange = (filter: any) => {
    setPortfolioFilters(filter);
  };

  const handleAIHighlight = (section: string) => {
    setHighlightedSection(section);
    setTimeout(() => setHighlightedSection(''), 3000);
  };

  const handleAIScroll = (target: string) => {
    setScrollTarget(target);
  };

  // Scroll to section when scrollTarget changes
  useEffect(() => {
    if (!scrollTarget) return;

    const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
      insights: insightsRef,
      composition: compositionRef,
      performance: performanceRef,
      risk: riskRef,
      history: historyRef,
      personalization: personalizationRef,
    };

    const ref = refMap[scrollTarget];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setTimeout(() => setScrollTarget(''), 1000);
  }, [scrollTarget]);

  // Render My Book View by default
  if (viewContext.level === 'my-book') {
    return <MyBookView />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Secondary Navigation */}
      <SecondaryNav />

      {/* Main Content - Centered Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!isGlobalMode && (
          <>
            {/* Portfolio Header */}
            {overview && (
              <div className="mb-6 bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-2xl font-bold text-[#002B45]">
                      Portfolio Dashboard
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                      Portfolio ID: {isPortfolioLevel ? viewContext.portfolioId : selectedPortfolio}
                    </p>
                    <div className="flex gap-6 mt-3 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Currency:</span> CHF
                      </div>
                      <div>
                        <span className="font-medium">Positions:</span> {positions.length}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-[#002B45]">
                      CHF {(overview.total_value / 1000000).toFixed(2)}M
                    </div>
                    <div className="text-sm text-gray-600">Total Value</div>
                    {overview.total_unrealized_pl !== undefined && (
                      <div className={`text-sm font-medium mt-2 ${
                        overview.total_unrealized_pl >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {overview.total_unrealized_pl >= 0 ? '+' : ''}
                        {((overview.total_unrealized_pl / overview.total_value) * 100).toFixed(2)}%
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* TASK-207: Portfolio Filters - Always show filters */}
            <PortfolioFilters
              filters={portfolioFilters}
              availableAccounts={availableAccounts}
              onFiltersChange={(partialFilters) => {
                setPortfolioFilters(partialFilters);
              }}
              historicalSupported={false} // TODO: Enable when backend supports historical snapshots
              clients={clientOptions}
              portfolios={portfolioOptions}
              showAccountsFilter={availableAccounts.length > 0}
              showAssetClassFilter={true}
              showCurrencyFilter={true}
              showDateFilter={false}
            />

            {/* Tab Navigation */}
            <div className="mb-6 bg-white rounded-lg shadow">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  {['insights', 'composition', 'performance', 'risk', 'personalization', 'history'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => onTabChange(tab)}
                      className={`px-6 py-4 text-sm font-medium capitalize border-b-2 transition-colors ${
                        activeTab === tab
                          ? 'border-[#002B45] text-[#002B45]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {activeTab === 'insights' && (
                  <div ref={insightsRef} className={highlightedSection === 'insights' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}>
                    <PortfolioInsightsTab
                      clientId={viewContext.clientId}
                      allocation={allocation}
                      risk={riskMetrics}
                      stressTests={stressTests}
                      performance={performance}
                      portfolioValue={overview?.total_value}
                      onAction={(action, _data) => {
                        switch (action) {
                          case 'rebalance':
                          case 'strategic-rebalance':
                          case 'increase-cash':
                          case 'stabilize':
                            onTabChange('risk');
                            break;
                          case 'analyze':
                          case 'review':
                            onTabChange('performance');
                            break;
                          case 'tax-loss-harvesting':
                            onTabChange('composition');
                            break;
                        }
                      }}
                      loading={loading}
                    />
                  </div>
                )}

                {activeTab === 'composition' && (
                  <div ref={compositionRef} className={highlightedSection === 'composition' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}>
                    <CompositionTab
                      positions={positions}
                      allocation={allocation}
                      cashPositions={cashPositions}
                      portfolioValue={overview?.total_value || 0}
                      filters={portfolioFilters}
                      clientId={viewContext.clientId}
                    />
                  </div>
                )}

                {activeTab === 'performance' && (
                  <div ref={performanceRef} className={highlightedSection === 'performance' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}>
                    <PerformanceTab
                      mode={isGlobalMode ? 'global' : 'client'}
                      metrics={performance}
                      history={performanceHistory}
                      attribution={performanceAttribution}
                      rollingReturns={rollingReturns}
                      rollingReturns12m={rollingReturns12m}
                      topContributors={topContributors}
                      bottomContributors={bottomContributors}
                      timeRange={_timeRange}
                      onTimeRangeChange={_onTimeRangeChange}
                      loading={loading}
                      benchmark={benchmark}
                      onBenchmarkChange={onBenchmarkChange}
                      clientId={viewContext.clientId}
                    />
                  </div>
                )}

                {activeTab === 'risk' && (
                  <div ref={riskRef} className={highlightedSection === 'risk' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}>
                    <RiskTab
                      mode={isGlobalMode ? 'global' : 'client'}
                      riskMetrics={riskMetrics}
                      stressTests={stressTests}
                      correlationMatrix={correlationMatrix}
                      varDistribution={varDistribution}
                      positions={positions}
                      portfolioValue={overview?.total_value}
                      performanceHistory={performanceHistory}
                      performanceMetrics={performance}
                      loading={loading}
                      clientId={!isGlobalMode ? (viewContext.clientId || selectedPortfolio) : undefined}
                      portfolioId={isPortfolioLevel ? viewContext.portfolioId : undefined}
                    />
                  </div>
                )}

                {activeTab === 'history' && (
                  <div ref={historyRef} className={highlightedSection === 'history' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}>
                    <HistoryTab
                      clientId={viewContext.clientId || selectedPortfolio}
                      transactions={transactions}
                    />
                  </div>
                )}

                {activeTab === 'personalization' && (
                  <div ref={personalizationRef} className={highlightedSection === 'personalization' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}>
                    <PersonalizationTab
                      clientId={viewContext.clientId || selectedPortfolio}
                    />
                  </div>
                )}
            </div>
          </>
        )}

        {/* Global Mode - Clients Tab */}
        {isGlobalMode && (
          <>
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-[#002B45]">Portfolio Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">
                Overview of all client portfolios
              </p>
            </div>

            {/* TASK-207: Portfolio Filters - Show in global mode too */}
            <div className="mb-6">
              <PortfolioFilters
                filters={portfolioFilters}
                availableAccounts={[]}
                onFiltersChange={(partialFilters) => {
                  setPortfolioFilters(partialFilters);
                }}
                historicalSupported={false} // TODO: Enable when backend supports historical snapshots
                clients={clientOptions}
                portfolios={portfolioOptions}
                showAccountsFilter={false}
                showAssetClassFilter={false}
                showCurrencyFilter={false}
                showDateFilter={false}
              />
            </div>

            <div className="bg-white rounded-lg shadow">
              <ClientsTab
                portfolios={clientPortfolios}
                onClientSelect={onClientSelect}
                onAction={(action, clientId) => {
                  switch (action) {
                    case 'prepare-meeting':
                      if (clientId) navigate(`/clients/${clientId}`);
                      break;
                    case 'refresh':
                      if (clientId) queryClient.invalidateQueries({ queryKey: ['client-dashboard', clientId] });
                      break;
                  }
                }}
              />
            </div>
          </>
        )}
      </main>

      {/* AI Portfolio Assistant - Available in all views */}
      {!isGlobalMode && clientData && (
        <PortfolioAIChat
          clientData={clientData}
          onTabChange={handleAITabChange}
          onFilterChange={handleAIFilterChange}
          onHighlight={handleAIHighlight}
          onScroll={handleAIScroll}
        />
      )}
    </div>
  );
};

export default PortfolioDashboardPresenter;
